<template>
  <div class="fac-dash">
    <header class="fac-dash__page-head">
      <div class="fac-dash__page-head-text">
        <h1 class="fac-dash__page-title">라인 보드</h1>
        <!-- <p class="fac-dash__page-sub">
          가동·OEE·공정 온도 등 샘플 지표입니다. 실제 설비와 무관한 데모
          화면입니다.
        </p> -->
      </div>
      <div class="fac-dash__page-head-actions">
        <el-select
          v-model="focusLine"
          size="small"
          placeholder="보기 기준"
          class="fac-dash__line-select"
          clearable
          @clear="focusLine = ''"
        >
          <el-option label="전체 (공장 집계)" value="" />
          <el-option label="라인 A" value="a" />
          <el-option label="라인 B" value="b" />
          <el-option label="라인 C" value="c" />
        </el-select>
        <el-tooltip
          content="온도 곡선이 주기적으로 움직입니다"
          placement="bottom"
        >
          <div class="fac-dash__sim-wrap">
            <span class="fac-dash__sim-label">온도 실시간 시뮬</span>
            <el-switch
              v-model="liveSimEnabled"
              active-color="#254aa5"
              @change="onLiveSimChange"
            />
          </div>
        </el-tooltip>
        <el-button
          type="default"
          size="small"
          icon="el-icon-download"
          class="fac-dash__head-btn"
          @click="onDemoExport"
        >
          내보내기
        </el-button>
        <el-tooltip content="시계열을 다시 그립니다" placement="bottom">
          <el-button
            type="default"
            size="small"
            icon="el-icon-refresh"
            circle
            class="fac-dash__refresh"
            @click="onRefresh"
          />
        </el-tooltip>
      </div>
    </header>

    <div class="fac-dash__alerts">
      <el-alert
        title="주의: 소결로 존2 온도가 상한(880°C)에 2°C 근접했습니다."
        type="warning"
        show-icon
        :closable="false"
      />
    </div>

    <section class="fac-dash__lines">
      <div
        v-for="line in lineStatuses"
        :key="line.id"
        class="fac-dash__line-pill"
        :class="{ 'is-focused': focusLine === line.id }"
        role="button"
        tabindex="0"
        @click="toggleLineFocus(line.id)"
        @keydown.enter.prevent="toggleLineFocus(line.id)"
      >
        <span class="fac-dash__line-name">{{ line.name }}</span>
        <el-tag :type="line.tagType" size="small" effect="dark">{{
          line.status
        }}</el-tag>
        <span class="fac-dash__line-meta">{{ line.meta }}</span>
      </div>
    </section>

    <section class="fac-dash__kpis">
      <el-card
        v-for="item in kpis"
        :key="item.key"
        class="fac-dash__kpi-card"
        shadow="hover"
      >
        <p class="fac-dash__kpi-label">{{ item.label }}</p>
        <p class="fac-dash__kpi-value">{{ item.value }}</p>
        <p class="fac-dash__kpi-meta">{{ item.meta }}</p>
      </el-card>
    </section>

    <el-row :gutter="20" class="fac-dash__row">
      <el-col :xs="24" :lg="16">
        <el-card class="fac-dash__chart-card" shadow="never">
          <div slot="header" class="fac-dash__card-head">
            <span>공정 온도 트렌드 (최근 24시간)</span>
            <span v-if="!focusLine" class="fac-dash__card-head-note">
              · 공장 집계 (대표 곡선)
            </span>
            <span v-else class="fac-dash__card-head-note">
              · {{ lineFocusLabel }} 기준
            </span>
          </div>
          <apexchart
            :key="'temp-' + chartRevision + '-' + focusLine"
            type="area"
            height="320"
            :options="tempChartOptions"
            :series="displayTempSeries"
          />
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="8">
        <el-card class="fac-dash__chart-card" shadow="never">
          <div slot="header" class="fac-dash__card-head">
            <span>OEE 종합</span>
            <span v-if="!focusLine" class="fac-dash__card-head-note">
              · 공장 집계
            </span>
            <span v-else class="fac-dash__card-head-note">
              · {{ lineFocusLabel }}
            </span>
          </div>
          <apexchart
            :key="'oee-r-' + chartRevision + '-' + focusLine"
            type="radialBar"
            height="320"
            :options="oeeRadialOptions"
            :series="displayOeeRadialSeries"
          />
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" class="fac-dash__row">
      <el-col :xs="24" :lg="12">
        <el-card class="fac-dash__chart-card" shadow="never">
          <div slot="header" class="fac-dash__card-head">
            <span>OEE 구성 (가용 · 성능 · 품질)</span>
            <span v-if="!focusLine" class="fac-dash__card-head-note">
              · 공장 집계
            </span>
            <span v-else class="fac-dash__card-head-note">
              · {{ lineFocusLabel }}
            </span>
          </div>
          <apexchart
            :key="'oee-b-' + chartRevision + '-' + focusLine"
            type="bar"
            height="300"
            :options="oeeBarOptions"
            :series="displayOeeBarSeries"
          />
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card class="fac-dash__chart-card" shadow="never">
          <div slot="header" class="fac-dash__card-head">
            <span>라인별 OEE (%)</span>
            <span v-if="!focusLine" class="fac-dash__card-head-note">
              · 라인 간 비교 (공장 보기에서도 동일)
            </span>
            <span v-else class="fac-dash__card-head-note">
              · 선택 라인 강조
            </span>
          </div>
          <apexchart
            :key="'line-oee-' + chartRevision + '-' + focusLine"
            type="bar"
            height="300"
            :options="lineOeeBarChartOptions"
            :series="lineOeeBarDisplaySeries"
          />
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="20" class="fac-dash__row">
      <el-col :xs="24">
        <el-card class="fac-dash__chart-card" shadow="never">
          <div slot="header" class="fac-dash__card-head">
            <span>최근 설비 알람</span>
            <span v-if="!focusLine" class="fac-dash__card-head-note">
              · 전체 라인
            </span>
            <span v-else class="fac-dash__card-head-note">
              · {{ lineFocusLabel }}만 표시
            </span>
          </div>
          <el-table
            :data="filteredAlarms"
            stripe
            size="small"
            class="fac-dash__table"
            @row-click="onAlarmRowClick"
          >
            <el-table-column prop="time" label="시각" width="88" />
            <el-table-column prop="level" label="등급" width="72">
              <template slot-scope="scope">
                <el-tag
                  :type="alarmTag(scope.row.level)"
                  size="mini"
                  effect="plain"
                >
                  {{ scope.row.level }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="line" label="라인" width="72" />
            <el-table-column prop="message" label="내용" min-width="220" />
          </el-table>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
const PRIMARY = "#254aa5";
const PRIMARY_LIGHT = "#4a90e2";
const MUTED = "#8b95a5";
const OK = "#67c23a";
const WARN = "#e6a23c";

const HOURS = Array.from(
  { length: 24 },
  (_, i) => `${String(i).padStart(2, "0")}:00`
);

function buildZoneTemps(seed) {
  const z1 = HOURS.map((_, i) => {
    const base = 820 + (seed % 15);
    const wave = Math.sin((i / 24) * Math.PI * 2) * 18;
    const lunch = i >= 11 && i <= 13 ? 12 : 0;
    return Math.round(base + wave + lunch + ((i * 5 + seed) % 7));
  });
  const z2 = z1.map((v, i) =>
    Math.min(890, Math.round(v + 35 + Math.sin(i / 3) * 6))
  );
  return { z1, z2 };
}

/** 라인 선택 시 KPI·OEE에 넣는 데모용 가짜 수치 (실제 집계와 무관) */
const LINE_DEMO = {
  a: {
    run: "95.1%",
    runMeta: "목표 92% 이상 · 라인 A",
    oee: "88.2%",
    oeeMeta: "A×P×Q · 라인 A",
    tph: "1,280 / 1,200",
    tphMeta: "단위: EA · 라인 A",
    tempMeta: "스펙 800~880°C · 라인 A 존1",
    oeeRadial: 88.2,
    oeeBar: [93.5, 95.2, 98.8],
    tempOffset: 0,
  },
  b: {
    run: "93.4%",
    runMeta: "목표 92% 이상 · 라인 B",
    oee: "85.1%",
    oeeMeta: "A×P×Q · 라인 B",
    tph: "1,190 / 1,200",
    tphMeta: "단위: EA · 라인 B",
    tempMeta: "스펙 800~880°C · 라인 B 존1",
    oeeRadial: 85.1,
    oeeBar: [91.0, 93.1, 99.5],
    tempOffset: -5,
  },
  c: {
    run: "78.0%",
    runMeta: "목표 92% 이상 · 라인 C (점검 영향)",
    oee: "72.4%",
    oeeMeta: "A×P×Q · 라인 C",
    tph: "980 / 1,200",
    tphMeta: "단위: EA · 라인 C",
    tempMeta: "스펙 800~880°C · 라인 C 존1",
    oeeRadial: 72.4,
    oeeBar: [88.0, 82.0, 95.0],
    tempOffset: 6,
  },
};

export default {
  name: "FactoryLineDashboard",
  data() {
    const seed = 7;
    const { z1, z2 } = buildZoneTemps(seed);

    return {
      chartRevision: 0,
      focusLine: "",
      liveSimEnabled: true,
      liveTimer: null,
      lineStatuses: [
        {
          id: "a",
          name: "라인 A",
          status: "가동",
          tagType: "success",
          meta: "목표 대비 102%",
        },
        {
          id: "b",
          name: "라인 B",
          status: "가동",
          tagType: "success",
          meta: "목표 대비 98%",
        },
        {
          id: "c",
          name: "라인 C",
          status: "점검",
          tagType: "warning",
          meta: "예정 정비 16:00",
        },
      ],
      kpisAggregate: [
        {
          key: "run",
          label: "설비 가동률 (당일)",
          value: "94.2%",
          meta: "목표 92% 이상",
        },
        {
          key: "oee",
          label: "OEE (종합)",
          value: "86.5%",
          meta: "A×P×Q 기준",
        },
        {
          key: "tph",
          label: "시간당 생산 (실적/목표)",
          value: "1,240 / 1,200",
          meta: "단위: EA",
        },
        {
          key: "temp",
          label: "평균 공정온도 (존1)",
          value: `${Math.round(z1.reduce((a, b) => a + b, 0) / z1.length)}°C`,
          meta: "스펙 800~880°C",
        },
      ],
      aggregateTempSeries: [
        { name: "존1 소결로 (°C)", data: z1 },
        { name: "존2 소결로 (°C)", data: z2 },
      ],
      tempChartOptions: {
        chart: {
          toolbar: { show: false },
          fontFamily: "Pretendard, sans-serif",
        },
        colors: [WARN, "#f56c6c"],
        stroke: { curve: "smooth", width: 2 },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.22,
            opacityTo: 0.02,
            stops: [0, 92, 100],
          },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: HOURS,
          tickAmount: 8,
          labels: { style: { colors: MUTED, fontSize: "10px" } },
        },
        yaxis: {
          labels: { style: { colors: MUTED } },
          title: { text: "°C", style: { color: MUTED, fontSize: "11px" } },
        },
        annotations: {
          yaxis: [
            {
              y: 880,
              borderColor: "#f56c6c",
              strokeDashArray: 4,
              label: {
                text: "상한 880°C",
                style: { color: "#f56c6c", fontSize: "11px" },
              },
            },
          ],
        },
        grid: { borderColor: "#eef0f3", strokeDashArray: 4 },
        legend: { position: "top", horizontalAlign: "right", fontSize: "12px" },
        tooltip: { y: { formatter: (v) => `${Math.round(v)} °C` } },
      },
      oeeRadialAggregate: [86.5],
      oeeBarAggregate: [92.1, 94.8, 99.2],
      lineOeeValues: [88.2, 85.1, 72.4],
      oeeRadialOptions: {
        chart: { fontFamily: "Pretendard, sans-serif" },
        plotOptions: {
          radialBar: {
            hollow: { size: "64%" },
            dataLabels: {
              name: {
                show: true,
                fontSize: "13px",
                color: MUTED,
                offsetY: -4,
              },
              value: {
                fontSize: "28px",
                fontWeight: 700,
                color: "#303133",
                formatter: (val) => `${val}%`,
              },
            },
          },
        },
        labels: ["OEE"],
        colors: [PRIMARY],
      },
      oeeBarOptions: {
        chart: {
          toolbar: { show: false },
          fontFamily: "Pretendard, sans-serif",
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "48%",
            borderRadius: 6,
            distributed: true,
            dataLabels: { position: "top" },
          },
        },
        colors: [PRIMARY, PRIMARY_LIGHT, OK],
        dataLabels: {
          enabled: true,
          formatter: (v) => `${v}%`,
          offsetY: -18,
          style: { fontSize: "11px", colors: ["#303133"] },
        },
        xaxis: {
          categories: ["가용성(A)", "성능(P)", "품질(Q)"],
          labels: { style: { colors: MUTED } },
        },
        yaxis: {
          max: 100,
          labels: { style: { colors: MUTED } },
        },
        grid: { borderColor: "#eef0f3" },
        legend: { show: false },
        tooltip: { y: { formatter: (v) => `${v}%` } },
      },
      lineOeeBarOptionsBase: {
        chart: {
          toolbar: { show: false },
          fontFamily: "Pretendard, sans-serif",
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: "55%",
            borderRadius: 4,
            distributed: true,
          },
        },
        dataLabels: {
          enabled: true,
          formatter: (v) => `${v}%`,
          style: { fontSize: "11px", colors: ["#fff"] },
        },
        xaxis: {
          categories: ["라인 A", "라인 B", "라인 C"],
          labels: { style: { colors: MUTED } },
        },
        yaxis: {
          labels: {
            style: { colors: MUTED, fontSize: "12px", fontWeight: 500 },
          },
        },
        grid: { borderColor: "#eef0f3" },
        legend: { show: false },
        tooltip: { y: { formatter: (v) => `${v}%` } },
      },
      alarms: [
        {
          time: "14:40",
          level: "W2",
          line: "B",
          message: "비전 검사 NG 연속 3회 — 재촬영 대기",
        },
        {
          time: "14:22",
          level: "I1",
          line: "A",
          message: "스핀들 부하 ±5% 이내 (정상)",
        },
        {
          time: "13:58",
          level: "W1",
          line: "C",
          message: "예방정비 타이머 도달 (잔여 45분)",
        },
        {
          time: "13:15",
          level: "E1",
          line: "A",
          message: "로봇 축2 토크 상한 접근 (점검 권고)",
        },
      ],
    };
  },
  computed: {
    lineFocusLabel() {
      const map = { a: "라인 A", b: "라인 B", c: "라인 C" };
      return map[this.focusLine] || "";
    },
    kpis() {
      if (!this.focusLine) {
        return this.kpisAggregate;
      }
      const p = LINE_DEMO[this.focusLine];
      if (!p) {
        return this.kpisAggregate;
      }
      const z = this.aggregateTempSeries[0].data;
      const avg =
        Math.round(z.reduce((a, b) => a + b, 0) / z.length) + p.tempOffset;
      return [
        {
          key: "run",
          label: "설비 가동률 (당일)",
          value: p.run,
          meta: p.runMeta,
        },
        {
          key: "oee",
          label: "OEE (종합)",
          value: p.oee,
          meta: p.oeeMeta,
        },
        {
          key: "tph",
          label: "시간당 생산 (실적/목표)",
          value: p.tph,
          meta: p.tphMeta,
        },
        {
          key: "temp",
          label: "평균 공정온도 (존1)",
          value: `${avg}°C`,
          meta: p.tempMeta,
        },
      ];
    },
    displayTempSeries() {
      if (!this.focusLine) {
        return this.aggregateTempSeries;
      }
      const off = LINE_DEMO[this.focusLine].tempOffset;
      return this.aggregateTempSeries.map((s) => ({
        name: s.name,
        data: s.data.map((v) =>
          Math.round(Math.min(895, Math.max(760, v + off)))
        ),
      }));
    },
    displayOeeRadialSeries() {
      if (!this.focusLine) {
        return this.oeeRadialAggregate;
      }
      return [LINE_DEMO[this.focusLine].oeeRadial];
    },
    displayOeeBarSeries() {
      if (!this.focusLine) {
        return [{ name: "비율", data: [...this.oeeBarAggregate] }];
      }
      return [
        {
          name: "비율",
          data: [...LINE_DEMO[this.focusLine].oeeBar],
        },
      ];
    },
    lineOeeBarDisplaySeries() {
      return [{ name: "OEE", data: [...this.lineOeeValues] }];
    },
    lineOeeBarColors() {
      const full = [PRIMARY, PRIMARY_LIGHT, WARN];
      if (!this.focusLine) {
        return full;
      }
      const idx = { a: 0, b: 1, c: 2 }[this.focusLine];
      const dim = "#d3d7de";
      return full.map((c, i) => (i === idx ? c : dim));
    },
    lineOeeBarChartOptions() {
      return {
        ...this.lineOeeBarOptionsBase,
        colors: this.lineOeeBarColors,
      };
    },
    filteredAlarms() {
      if (!this.focusLine) {
        return this.alarms;
      }
      const map = { a: "A", b: "B", c: "C" };
      const letter = map[this.focusLine];
      return this.alarms.filter((a) => a.line === letter);
    },
  },
  watch: {
    focusLine() {
      this.chartRevision += 1;
    },
  },
  mounted() {
    if (this.liveSimEnabled) {
      this.startLiveTick();
    }
  },
  beforeDestroy() {
    this.stopLiveTick();
  },
  methods: {
    toggleLineFocus(id) {
      this.focusLine = this.focusLine === id ? "" : id;
    },
    onAlarmRowClick(row) {
      this.$message.info(`${row.line}라인 · ${row.message}`);
    },
    onDemoExport() {
      this.$message.info("데모 환경에서는 내보내기를 지원하지 않습니다.");
    },
    onLiveSimChange(enabled) {
      if (enabled) {
        this.startLiveTick();
      } else {
        this.stopLiveTick();
      }
    },
    startLiveTick() {
      this.stopLiveTick();
      this.liveTimer = setInterval(() => this.tickLiveTemps(), 1000);
    },
    stopLiveTick() {
      if (this.liveTimer) {
        clearInterval(this.liveTimer);
        this.liveTimer = null;
      }
    },
    tickLiveTemps() {
      if (document.hidden || !this.liveSimEnabled) {
        return;
      }
      const s1 = [...this.aggregateTempSeries[0].data];
      const s2 = [...this.aggregateTempSeries[1].data];
      s1.shift();
      s2.shift();
      const n1 = s1[s1.length - 1];
      const n2 = s2[s2.length - 1];
      s1.push(
        Math.min(880, Math.max(780, Math.round(n1 + (Math.random() * 8 - 4))))
      );
      s2.push(
        Math.min(895, Math.max(800, Math.round(n2 + (Math.random() * 8 - 4))))
      );
      this.aggregateTempSeries = [
        { name: this.aggregateTempSeries[0].name, data: s1 },
        { name: this.aggregateTempSeries[1].name, data: s2 },
      ];
      const avg = Math.round(s1.reduce((a, b) => a + b, 0) / s1.length);
      this.kpisAggregate = this.kpisAggregate.map((row, i) =>
        i === 3 ? { ...row, value: `${avg}°C` } : row
      );
    },
    alarmTag(level) {
      if (level.startsWith("E")) {
        return "danger";
      }
      if (level.startsWith("W")) {
        return "warning";
      }
      return "info";
    },
    onRefresh() {
      const seed = Date.now() % 997;
      const { z1, z2 } = buildZoneTemps(seed);
      this.aggregateTempSeries = [
        { name: "존1 소결로 (°C)", data: z1 },
        { name: "존2 소결로 (°C)", data: z2 },
      ];
      const avg = Math.round(z1.reduce((a, b) => a + b, 0) / z1.length);
      const oee = (82 + (seed % 12) + (seed % 10) / 10).toFixed(1);
      const oeeNum = Number(oee);
      this.oeeRadialAggregate = [oeeNum];
      this.oeeBarAggregate = [
        Math.min(99, 90 + (seed % 8) + (seed % 3) / 10),
        Math.min(99, 92 + (seed % 7) + (seed % 4) / 10),
        Math.min(99.5, 97 + (seed % 3) + (seed % 2) / 10),
      ].map((v) => Math.round(v * 10) / 10);
      this.lineOeeValues = [
        Math.min(95, oeeNum + (seed % 5) - 1 + (seed % 3) / 10),
        Math.min(92, oeeNum - 2 - (seed % 4) + (seed % 2) / 10),
        Math.max(62, oeeNum - 14 - (seed % 6) + (seed % 2) / 10),
      ].map((v) => Math.round(v * 10) / 10);
      this.kpisAggregate = [
        {
          ...this.kpisAggregate[0],
          value: `${(90 + (seed % 8)).toFixed(1)}%`,
        },
        { ...this.kpisAggregate[1], value: `${oee}%` },
        {
          ...this.kpisAggregate[2],
          value: `${1180 + (seed % 120)} / 1,200`,
        },
        { ...this.kpisAggregate[3], value: `${avg}°C` },
      ];
      this.chartRevision += 1;
      this.$message.success("라인 데이터를 갱신했습니다.");
    },
  },
};
</script>

<style lang="scss" scoped>
.fac-dash {
  width: 100%;
  padding: 24px 32px 48px;
  box-sizing: border-box;
  background: linear-gradient(180deg, #f4f6f8 0%, #fff 30%);
}

.fac-dash__page-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}

.fac-dash__page-head-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.fac-dash__line-select {
  width: 132px;
}

.fac-dash__sim-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.fac-dash__sim-label {
  font-size: 12px;
  color: #606266;
  white-space: nowrap;
}

.fac-dash__head-btn {
  flex-shrink: 0;
}

.fac-dash__refresh {
  flex-shrink: 0;
}

.fac-dash__page-title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 700;
  color: #303133;
  letter-spacing: -0.03em;
}

.fac-dash__page-sub {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: #909399;
}

.fac-dash__page-hint {
  margin: 10px 0 0;
  max-width: 720px;
  font-size: 12px;
  line-height: 1.65;
  color: #909399;
}

.fac-dash__page-hint strong {
  font-weight: 600;
  color: #606266;
}

.fac-dash__alerts {
  margin-bottom: 16px;
}

.fac-dash__alerts ::v-deep .el-alert {
  border-radius: 8px;
}

.fac-dash__lines {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 18px;
}

.fac-dash__line-pill {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 14px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.fac-dash__line-pill.is-focused {
  border-color: #254aa5;
  box-shadow: 0 0 0 1px rgba(37, 74, 165, 0.18);
}

.fac-dash__line-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.fac-dash__line-meta {
  font-size: 12px;
  color: #909399;
}

.fac-dash__kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 20px;
}

@media (max-width: 992px) {
  .fac-dash__kpis {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .fac-dash__kpis {
    grid-template-columns: 1fr;
  }
}

.fac-dash__kpi-card {
  border-radius: 8px;
  border: 1px solid #ebeef5;
}

.fac-dash__kpi-label {
  margin: 0 0 6px;
  font-size: 12px;
  color: #909399;
}

.fac-dash__kpi-value {
  margin: 0 0 4px;
  font-size: 19px;
  font-weight: 700;
  color: #303133;
  letter-spacing: -0.02em;
}

.fac-dash__kpi-meta {
  margin: 0;
  font-size: 11px;
  color: #67c23a;
}

.fac-dash__row {
  margin-bottom: 20px !important;
}

@media (max-width: 1199px) {
  .fac-dash__row > .el-col:not(:last-child) {
    margin-bottom: 20px;
  }
}

.fac-dash__chart-card {
  border-radius: 8px;
  border: 1px solid #ebeef5;
  margin-bottom: 0;
}

.fac-dash__card-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.fac-dash__card-head-note {
  font-size: 12px;
  font-weight: 500;
  color: #909399;
}

.fac-dash__table {
  width: 100%;
}

.fac-dash__table ::v-deep .el-table__row {
  cursor: pointer;
}

.fac-dash__footnote {
  margin: 8px 4px 0;
  font-size: 12px;
  color: #909399;
  text-align: right;
}
</style>
